import { useMemo } from 'react'
import type { JSX } from 'react'

import {
  ChartSeriesProvider,
  ComposedChart,
  pickBucketGranularity,
} from '../charts'
import type { ChartAnnotation } from '../charts'
import type { MetricSeries } from '../MetricChart/types'

import { toChartSeriesInputs } from './chartSeriesInputs'

interface ContentImpactChartProps {
  /** Built chart series for the Content Impact view: posts (a count-per-day
   * Total series) + followers (a cumulative Latest series), each optionally
   * followed by a dashed comparison sibling. */
  metrics: MetricSeries[]
  startDate?: Date
  endDate?: Date
  /** When true, the x-axis shows only the comparison-period dates. */
  comparisonOnly?: boolean
  height?: number
  /** Disable enter animations (used by the offscreen export snapshot). */
  disableAnimations?: boolean
  /** Timeline flag markers (e.g. the channel's data-collection start), forwarded
   * to the underlying {@link ComposedChart}. */
  annotations?: ChartAnnotation[]
  /** Day-aligned x-axis ticks so date labels line up under annotation flags. */
  xAxisTicks?: number[]
  /** Fixed y-tick count, forwarded to {@link ComposedChart} — the export snapshot
   *  sets it so its taller card matches the on-screen tick count. */
  yTickCount?: number
}

/**
 * Content Impact chart: posts as bars, followers as a line. A thin domain wrapper
 * that maps the view's `MetricSeries` into the new charts module's context and
 * renders the generic {@link ComposedChart} — the Total series draws as bars on
 * the left axis, the Latest series as a line on the right, and comparison
 * siblings as the muted variant (grouped lighter bars / a dashed line).
 *
 * @see ../charts/ComposedChart/ComposedChart
 */
export function ContentImpactChart({
  metrics,
  startDate,
  endDate,
  comparisonOnly = false,
  height,
  disableAnimations = false,
  annotations,
  xAxisTicks,
  yTickCount,
}: ContentImpactChartProps): JSX.Element {
  // Bucket wider ranges (week/month) so posts bars stay readable instead of
  // collapsing to hairlines; the chart labels ticks/tooltip to match.
  const granularity =
    startDate && endDate ? pickBucketGranularity(startDate, endDate) : 'day'
  const series = useMemo(
    () => toChartSeriesInputs(metrics, granularity),
    [metrics, granularity],
  )

  return (
    <ChartSeriesProvider series={series}>
      <ComposedChart
        ariaLabel="Content impact: posts and followers over time"
        startDate={startDate}
        endDate={endDate}
        comparisonOnly={comparisonOnly}
        granularity={granularity}
        height={height}
        disableAnimations={disableAnimations}
        annotations={annotations}
        xAxisTicks={xAxisTicks}
        yTickCount={yTickCount}
      />
    </ChartSeriesProvider>
  )
}
